import BoardCanvas from 'boardCanvas/BoardCanvas';
import {
  MaterialPatternString,
  parseMaterialPatternString,
  sumPattern,
} from 'Pattern';
import { useRecoilValue } from 'recoil';
import { BoardType, Units } from 'Types';

import BoardBase from 'boardClasses/BoardBase';
import { createAnonymousBoard } from 'boardClasses/BoardFactory';
import Strips from 'boardClasses/strips/Strips';
import StripsAdvanced from 'boardClasses/stripsAdvanced/StripsAdvanced';
import BuildStep from 'designer/BuildStep';
import { settingsAtom } from 'state/recoilState';
import { getDisplayLengthInUnits } from 'utils/utils';

type Props = {
  board: BoardBase<BoardType.Pinwheel>;
};

function PinwheelBuildSteps(props: Props) {
  const { board } = props;

  const settings = useRecoilValue(settingsAtom);

  const { kerf, planeBuffer } = settings;
  const { params, units } = board;
  const displayLength = getDisplayLengthInUnits(units);
  const { numHorizontal, numVertical, thickness } = params;
  const numBlocks = numHorizontal * numVertical;
  const step1Length =
    numBlocks * (thickness + planeBuffer) + (numBlocks - 1) * kerf;
  const step1Pattern = new Array(numHorizontal)
    .fill(board.params.pattern)
    .join(' .5 ') as MaterialPatternString;
  const blockSize = thickness + planeBuffer;

  const steps = [
    {
      title: 'Cut the strips',
      text: `First cut the strips for the initial glue up. You'll need a total of ${displayLength(
        step1Length
      )} in length. It's unlikely that you'll want to do this in all one glue up, so break it up into managable lengths.`,
      board: createAnonymousBoard(
        Strips,
        {
          pattern: step1Pattern,
          numRows: 1,
          rowHeight: step1Length / numHorizontal,
          thickness: 0,
          flip: false,
        },
        Units.IN
      ),
    },
    {
      title: 'Cut the blocks',
      text: `Next the strips into blocks ${displayLength(blockSize)} wide.`,
      board: createAnonymousBoard(
        StripsAdvanced,
        {
          pattern: new Array(numVertical)
            .fill(`${blockSize}a .5b`)
            .join(' ') as MaterialPatternString,
          subpatterns: [step1Pattern, '.5' as MaterialPatternString],
          thickness: 0,
        },
        Units.IN
      ),
    },
    {
      title: 'Rotate the blocks',
      text: `Rotate the blocks so the end grain is facing up`,
      board: createAnonymousBoard(
        StripsAdvanced,
        {
          pattern: new Array(numVertical)
            .fill(
              `${sumPattern(
                parseMaterialPatternString(board.params.pattern)
              )}a .5b`
            )
            .join(' ') as MaterialPatternString,
          subpatterns: [step1Pattern, '.5' as MaterialPatternString],
          thickness: 0,
        },
        Units.IN
      ),
    },
    {
      title: 'Arrange the blocks',
      text: `Arrange the blocks into the final pattern, glue them together, and plane the board to it's final thickness.`,
      board,
    },
  ];

  return (
    <>
      {steps.map((step, index) => (
        <BuildStep
          key={index}
          title={step.title}
          content={step.text}
          preview={
            <BoardCanvas
              dimensions={step.board.getDimensions()}
              renderables={step.board.getRenderables()}
              scaleFactor={24}
            />
          }
        ></BuildStep>
      ))}
    </>
  );
}

export default PinwheelBuildSteps;
